import { useCallback, useEffect, useRef, useState } from 'react'
import JuditProcessos from '@services/juditProcessos'
import { localProcessConsultations, processDocumentError, processDocumentText } from './processDocumentHelpers'
import { normalizeCnj, validCnj } from '../legal-rag/juditDocumentHelpers'
import { linkProcessDocumentSync, ProcessDocumentSyncError, processSyncProgress, processSyncReceiptForGrade, processSyncRemotePending,
  processSyncStorageKey, processSyncTarget, readProcessSyncReceipt, reconcileProcessDocumentSync, runProcessDocumentSync, runProcessDocumentReextraction,
  scopedProcessSyncReceiptWriter } from './ProcessDocumentsSync'

export default function useProcessDocuments(numeroProcesso, isOpen, actorId, canManageDocuments = false) {
  const scope = `${actorId || ''}|${normalizeCnj(numeroProcesso)}|${Boolean(isOpen)}`
  const identity = useRef({ scope })
  if (identity.current.scope !== scope) identity.current = { scope }
  const [stateScope, setStateScope] = useState(scope)
  const mounted = useRef(false)
  const [consultas, setConsultas] = useState([])
  const [maisConsultas, setMaisConsultas] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [visibleText, setVisibleText] = useState(null)
  const [textLoading, setTextLoading] = useState('')
  const [textError, setTextError] = useState('')
  const [syncPhase, setSyncPhase] = useState('')
  const [syncError, setSyncError] = useState('')
  const [activeReceiptId, setActiveReceiptId] = useState('')
  const [syncReceipts, setSyncReceipts] = useState([])
  const [syncProgress, setSyncProgress] = useState(null)
  const [instanceRequired, setInstanceRequired] = useState(false)
  const listRequest = useRef(null)
  const textRequest = useRef(null)
  const syncOperation = useRef(null)
  const consultasRef = useRef([])
  const receiptsRef = useRef([])

  const current = useCallback((epoch) => mounted.current && identity.current === epoch, [])
  const closeText = useCallback(() => {
    textRequest.current?.abort()
    textRequest.current = null
    setVisibleText(null)
    setTextLoading('')
    setTextError('')
  }, [])
  const applyConsulta = useCallback((confirmed, epoch) => {
    if (!current(epoch)) return
    const rows = [confirmed, ...consultasRef.current.filter((item) => item.consultaId !== confirmed.consultaId)]
    if (rows.length > 50) setMaisConsultas(true)
    consultasRef.current = rows.slice(0, 50)
    setConsultas(consultasRef.current)
  }, [current])

  useEffect(() => {
    const epoch = identity.current
    mounted.current = true
    consultasRef.current = []
    receiptsRef.current = []
    setConsultas([])
    setMaisConsultas(false)
    setError('')
    closeText()
    setSyncError('')
    setSyncPhase('')
    setSyncProgress(null)
    setInstanceRequired(false)
    setActiveReceiptId('')
    setSyncReceipts([])
    setStateScope(scope)
    if (isOpen) {
      try {
        const receipts = [null, 1, 2].map((instance) => readProcessSyncReceipt(window.sessionStorage, processSyncStorageKey(actorId, numeroProcesso, instance))).filter(Boolean)
        receiptsRef.current = receipts
        setSyncReceipts(receipts)
        const pending = receipts.filter((row) => !['FINISHED', 'FAILED'].includes(row.state))
        if (pending.length === 1) setActiveReceiptId(pending[0].requestId)
      } catch { setSyncError('Não foi possível ler os recibos locais. Qualquer sincronização dependerá de guardar e reler o recibo antes do envio.') }
    }
    return () => {
      mounted.current = false
      listRequest.current?.abort()
      textRequest.current?.abort()
      const operation = syncOperation.current
      if (operation?.epoch === epoch) {
        if (operation.lastReceipt) {
          try { operation.persist({ ...operation.lastReceipt, state: 'UNCERTAIN' }) } catch { /* Receipt remains in its original actor/process scope. */ }
        }
        operation.controller.abort()
        syncOperation.current = null
      }
    }
  }, [actorId, numeroProcesso, scope, isOpen, closeText])

  const reload = useCallback(async () => {
    if (syncOperation.current) return
    const epoch = identity.current
    listRequest.current?.abort()
    closeText()
    setError('')
    if (!isOpen || !validCnj(numeroProcesso) || !Number(actorId)) { setLoading(false); return }
    const controller = new AbortController()
    listRequest.current = controller
    setLoading(true)
    try {
      const response = await JuditProcessos.listByProcess(numeroProcesso, controller.signal)
      if (controller.signal.aborted || listRequest.current !== controller || !current(epoch)) return
      if (!response.success) throw new Error(processDocumentError(response))
      const rows = localProcessConsultations(response.data, numeroProcesso)
      consultasRef.current = rows
      setConsultas(rows)
      setMaisConsultas(response.data.maisConsultas === true)
    } catch (failure) {
      if (!controller.signal.aborted && listRequest.current === controller && current(epoch)) setError(failure.message)
    } finally {
      if (!controller.signal.aborted && listRequest.current === controller && current(epoch)) setLoading(false)
    }
  }, [actorId, numeroProcesso, isOpen, closeText, current])
  useEffect(() => { reload() }, [reload])

  const readText = async (consulta, anexo) => {
    if (syncOperation.current) return
    const epoch = identity.current
    closeText()
    const controller = new AbortController()
    textRequest.current = controller
    setTextLoading(anexo.anexoId)
    try {
      const response = await JuditProcessos.text(consulta.consultaId, anexo.documentoId, controller.signal)
      if (controller.signal.aborted || textRequest.current !== controller || !current(epoch)) return
      if (!response.success) throw new Error(processDocumentError(response))
      setVisibleText(processDocumentText(response.data, consulta, anexo))
    } catch (failure) {
      if (!controller.signal.aborted && textRequest.current === controller && current(epoch)) setTextError(failure.message)
    } finally {
      if (!controller.signal.aborted && textRequest.current === controller && current(epoch)) setTextLoading('')
    }
  }

  const makeOperation = (initialReceipt) => {
    const epoch = identity.current
    const controller = new AbortController()
    let storage
    try { storage = window.sessionStorage }
    catch { throw new ProcessDocumentSyncError('O navegador não permite guardar o recibo. Nenhuma solicitação foi enviada.', { code: 'LOCAL_RECEIPT_REQUIRED' }) }
    const operation = { epoch, controller, lastReceipt: initialReceipt }
    const writer = scopedProcessSyncReceiptWriter({ storage, actorId, numeroProcesso, initialReceipt,
      isCurrent: () => current(epoch), onStale: () => controller.abort(), onCurrent: (stored) => {
        const previousId = operation.previousRequestId
        receiptsRef.current = [...receiptsRef.current.filter((row) => row.instancia !== stored.instancia && row.requestId !== previousId), stored]
        setSyncReceipts(receiptsRef.current)
        setActiveReceiptId(stored.requestId)
      } })
    operation.persist = (receipt) => { operation.previousRequestId = operation.lastReceipt?.requestId; operation.lastReceipt = receipt; return writer(receipt) }
    syncOperation.current = operation
    closeText()
    return operation
  }

  const synchronize = async (consulta, instance) => {
    if (!canManageDocuments || consulta && !consulta.podeAlterar || syncOperation.current || loading || !validCnj(numeroProcesso)) return null
    const target = processSyncTarget(consulta, instance)
    const selected = target.consulta
    const grade = target.instancia
    const previous = processSyncReceiptForGrade(receiptsRef.current, grade, activeReceiptId)
    if (previous && ['REQUESTING', 'UNCERTAIN'].includes(previous.state)) { setSyncError('Reconcilie ou vincule explicitamente o recibo deste grau antes de qualquer novo envio.'); return null }
    const resume = previous?.consultaId && previous.state !== 'FINISHED'
    if (resume && selected && selected.consultaId !== previous.consultaId) { setSyncError('Este grau tem uma operação em andamento em outra consulta. Selecione a consulta desse recibo para continuar.'); return null }
    const candidates = grade ? consultasRef.current.filter((row) => row.instancia === grade) : consultasRef.current
    if (!resume && (candidates.length > 0 || maisConsultas && !grade) && !selected) { setSyncError('Selecione uma consulta pelo grau e data antes de sincronizar.'); return null }
    let operation
    try { operation = makeOperation(resume ? previous : null) }
    catch (failure) { setSyncError(failure.message); return null }
    const input = { numeroProcesso, requestId: resume ? previous.requestId : crypto.randomUUID(),
      ...((resume ? previous.instancia : grade) ? { instancia: resume ? previous.instancia : grade } : {}),
      ...(!resume && selected ? { consultaId: selected.consultaId, versao: selected.versao } : {}),
      reuseOnly: Boolean(selected && processSyncRemotePending(selected)) }
    setSyncError('')
    setInstanceRequired(false)
    setSyncPhase('Sincronizando documentos…')
    try {
      const result = await runProcessDocumentSync({ api: JuditProcessos, input, previousReceipt: resume ? previous : null,
        signal: operation.controller.signal, onReceipt: operation.persist,
        onConsulta: (row) => applyConsulta(row, operation.epoch),
        onProgress: (progress) => { if (current(operation.epoch)) setSyncProgress(progress) } })
      return current(operation.epoch) ? result.consulta : null
    } catch (failure) {
      if (current(operation.epoch)) {
        setSyncError(failure.message || 'A sincronização foi interrompida. Confira o recibo antes de continuar.')
        if (failure.code === 'JUDIT_INSTANCE_REQUIRED') setInstanceRequired(true)
        if (failure.uncertain) setSyncProgress((progress) => ({ ...(progress || {}), phase: 'UNCERTAIN' }))
      }
      return null
    } finally {
      if (syncOperation.current === operation) { syncOperation.current = null; if (current(operation.epoch)) setSyncPhase('') }
    }
  }

  const reextract = async (consulta, anexo) => {
    if (!canManageDocuments || !consulta?.podeAlterar || syncOperation.current || loading || !validCnj(numeroProcesso)) return null
    const previous = processSyncReceiptForGrade(receiptsRef.current, consulta.instancia, activeReceiptId)
    let operation
    try { operation = makeOperation(previous) }
    catch (failure) { setSyncError(failure.message); return null }
    setSyncError('')
    setSyncPhase('Obtendo novamente e extraindo o documento…')
    try {
      const result = await runProcessDocumentReextraction({ api: JuditProcessos, consulta, anexo, requestId: crypto.randomUUID(), previousReceipt: previous,
        signal: operation.controller.signal, onReceipt: operation.persist, onConsulta: (row) => applyConsulta(row, operation.epoch) })
      if (current(operation.epoch)) setSyncProgress(processSyncProgress(result.consulta, 'FINISHED'))
      return current(operation.epoch) ? result.consulta : null
    } catch (failure) {
      if (current(operation.epoch)) {
        setSyncError(failure.message || 'A nova obtenção não foi confirmada. Releia o recibo antes de continuar.')
        if (failure.uncertain) setSyncProgress((progress) => ({ ...(progress || {}), phase: 'UNCERTAIN' }))
      }
      return null
    } finally {
      if (syncOperation.current === operation) { syncOperation.current = null; if (current(operation.epoch)) setSyncPhase('') }
    }
  }

  const recover = async (requestId, selected = null) => {
    const receipt = receiptsRef.current.find((row) => row.requestId === requestId)
    if (syncOperation.current || !receipt) return null
    let operation
    try { operation = makeOperation(receipt) }
    catch (failure) { setSyncError(failure.message); return null }
    setSyncPhase(selected ? 'Vinculando a consulta local escolhida…' : 'Reconciliando o recibo local…')
    setSyncError('')
    try {
      const params = { api: JuditProcessos, receipt, numeroProcesso, signal: operation.controller.signal }
      const result = selected ? await linkProcessDocumentSync({ ...params, selected }) : await reconcileProcessDocumentSync(params)
      operation.persist(result.receipt)
      applyConsulta(result.consulta, operation.epoch)
      if (current(operation.epoch)) setSyncProgress(processSyncProgress(result.consulta, result.receipt.state === 'UNCERTAIN' ? 'UNCERTAIN' : 'WAITING'))
      return current(operation.epoch) ? result.consulta : null
    } catch (failure) {
      if (current(operation.epoch)) setSyncError(failure.message || 'Não foi possível confirmar o recibo local.')
      return null
    } finally {
      if (syncOperation.current === operation) { syncOperation.current = null; if (current(operation.epoch)) setSyncPhase('') }
    }
  }
  const chooseSyncReceipt = (requestId) => {
    if (syncOperation.current || !receiptsRef.current.some((row) => row.requestId === requestId)) return
    setActiveReceiptId(requestId)
    setSyncError('')
  }
  const safe = stateScope === scope
  const active = safe ? syncReceipts.find((row) => row.requestId === activeReceiptId) || null : null
  return { consultas: safe ? consultas : [], maisConsultas: safe && maisConsultas, loading: !safe || loading,
    error: safe ? error : '', visibleText: safe ? visibleText : null, textLoading: safe ? textLoading : '', textError: safe ? textError : '',
    reload, readText, closeText, syncPhase: safe ? syncPhase : '', syncError: safe ? syncError : '', syncReceipt: active,
    syncReceipts: safe ? syncReceipts : [], syncProgress: safe ? syncProgress : null, instanceRequired: safe && instanceRequired,
    synchronize, reextract, reconcileSync: (requestId) => recover(requestId), linkSyncReceipt: (requestId, selected) => recover(requestId, selected), chooseSyncReceipt,
    syncUncertain: Boolean(active && ['REQUESTING', 'UNCERTAIN'].includes(active.state)) }
}
